import React, {useCallback} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {trackPromise, usePromiseTracker} from "react-promise-tracker";
import {Subject} from "rxjs";
import {showToast} from "common";
import {useObservable} from "rxsupport";
import {FatOutlineButton} from "common/component/FatOutlineButton";
import {LoadingSpinner} from "common";
import {FsoInquiryByCurrentProviderInput} from "../../domain";
import {ModalTextAreaInput} from "../../form-components/ModalTextAreaInput";
import {useSubmitFsoInquiryByProvider} from "./useSubmitFsoInquriyByProvider";

type Props = {
    visible$: Subject<boolean>;
};

export function FSOInquiryFormModal({visible$}: Props) {
    const visible = useObservable(visible$, false);
    const {submitFsoInquiryByProvider} = useSubmitFsoInquiryByProvider();

    const methods = useForm<FsoInquiryByCurrentProviderInput>({
        defaultValues: {
            inquiry: ""
        }
    });

    const {promiseInProgress: isCallingBackend} = usePromiseTracker();

    const submitWithTracking = useCallback((formValues: FsoInquiryByCurrentProviderInput | null) => trackPromise(submitFsoInquiryByProvider(formValues?.inquiry || null)), [submitFsoInquiryByProvider]);

    const onCancel = useCallback(async () => {
        showToast.warning(`Submission for FSO Inquiry is cancelled`);
        methods.reset();
        visible$.next(false);
    }, [methods, visible$]);
    const submit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        await methods.handleSubmit(async (data: FsoInquiryByCurrentProviderInput) => {
            await submitWithTracking(data);
            methods.reset();
            visible$.next(false);
        })(e);
    }, [methods, visible$, submitWithTracking]);


    return (
        <>
            {visible && (
                <div className="fixed inset-0 z-50 flex justify-center pt-24" style={{backgroundColor: "rgba(0,0,0,0.4)"}}>
                    <div className="relative px-4 w-full max-w-4xl">
                        <div className="relative bg-white rounded-lg shadow dark:bg-gray-700">
                            <div className="flex justify-between items-start p-4 rounded-t border-b dark:border-gray-600">
                                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                                    FSO Inquiry
                                </h3>
                                <button type="button"
                                        className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white"
                                        disabled={isCallingBackend}
                                        onClick={onCancel}
                                >
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"
                                         xmlns="http://www.w3.org/2000/svg">
                                        <path fillRule="evenodd"
                                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                              clipRule="evenodd"/>
                                    </svg>
                                </button>
                            </div>

                            <FormProvider {...methods}>
                                <form autoComplete="off" onSubmit={submit}>
                                    {!isCallingBackend && (<>
                                        <div className="space-y-6">
                                            <div className="flex flex-col w-full p-4 scrollbar-always"
                                                 style={{maxHeight: "calc(100vh - 68px - 100px - 37px - 90px - 60px)"}}>
                                                <ModalTextAreaInput
                                                    disabled={isCallingBackend}
                                                    field="inquiry"
                                                    label="Please provide details for your FSO general inquiry."
                                                    placeholder="Type your inquiry details here."
                                                    labelPosition="top"
                                                    rowsNum={5}
                                                    maxLength={4000}
                                                    required
                                                    showCharacterCount
                                                    requiredMessage="Please provide details for your FSO inquiry before submitting."
                                                />
                                            </div>
                                        </div>

                                        <div className="w-full flex border-t dark:border-gray-600 p-4 justify-end">
                                            <div className="flex w-1/4">
                                                <FatOutlineButton color={'red'}
                                                                  text={"Cancel"}
                                                                  onClick={onCancel}/>
                                                <span style={{width: 10}}/>
                                                <FatOutlineButton type="submit" text={"Submit"} color={"green"}/>
                                            </div>
                                        </div>
                                    </>)}

                                    {isCallingBackend &&
                                        <div className="p-24">
                                            <LoadingSpinner text="Processing"/>
                                        </div>
                                    }
                                </form>
                            </FormProvider>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
